import IFileService from "data/IFileService";
import PageCard from "features/shared/components/card/PageCard";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import CreatableItem from "features/shared/components/creatableItem/CreatableItem";
import Icon from "features/shared/components/icons/Icon";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import DocumentDTO from "features/shared/dtos/DocumentDTO";
import FileUploadSubDialog from "features/shared/forms/components/file/FileUploadSubDialog";
import FormGroup from "features/shared/forms/components/FormGroup";
import { UseFormHookResult } from "features/shared/forms/hooks/useForm";
import { isEmptyOrUndf } from "features/shared/helpers/objects";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React, { useEffect } from "react";

type Props = {
  formHandler: UseFormHookResult;
};

const InvoiceUploadSubForm = ({ formHandler }: Props) => {
  //  DI
  const dialogService = useInjection<IContentDialogService>(
    IocTypes.ContentDialogService
  );

  const fileService = useInjection<IFileService>(IocTypes.FileService);

  //translations
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "invoiceUploadSubForm");

  // form hook props
  const { getFormValue, setFormValue, clearFormValues } = {
    ...formHandler,
  };

  // side-effects
  useEffect(() => {
    return () => {
      // before unmount component remove invoice
      clearFormValues(["currentSupplierInvoice"]);
    };
  }, []);

  // helpers
  const getUploadedInvoice = () => {
    return getFormValue<DocumentDTO>("currentSupplierInvoice");
  };

  // handlers
  const submitInvoiceUploadHandler = async (files: FileList) => {
    const file: File = files[0];
    const base64Content = await fileService.convertFileToBase64(file);

    const document: DocumentDTO = {
      base64Content: base64Content,
      fileNameWithExtension: file.name,
    };

    setFormValue("currentSupplierInvoice", document);
    dialogService.closeDialog();
  };

  const removeUploadeInvoiceHandler = (code: string) => {
    clearFormValues(["currentSupplierInvoice"]);
  };

  const openFileUpladDialogHandler = () => {
    dialogService.openDialog(
      tLabels("dialogTitle"),
      renderFileUploadSubDialog
    );
  };

  // sub-renders
  const renderFileUploadSubDialog = () => {
    return <FileUploadSubDialog onSubmit={submitInvoiceUploadHandler} />;
  };

  const renderUploadedFile = () => {
    const invoice = getUploadedInvoice();

    if (!isEmptyOrUndf(invoice)) {
      return (
        <div className="col-auto px-1">
          <CreatableItem
            text={invoice.fileNameWithExtension!}
            code={"uploadedInvoice"}
            onActionClick={removeUploadeInvoiceHandler}
          />
        </div>
      );
    } else {
      return (
        <div className="col-auto px-1">
          <CreatableItem
            text={tLabels("noUploadedInvoice")}
            code={"noUploadedInvoice"}
          />
        </div>
      );
    }
  };

  // render
  return (
    <PageCard
      title={tLabels("cardTitle")}
      bodyClassName="pt-4 px-4"
      cardClassName="mb-4"
    >
      <FormGroup labelText={tLabels("label")} className="mb-2">
        <div className="row mx-n1">
          {renderUploadedFile()}
          <RenderWrapper isVisible={() => isEmptyOrUndf(getUploadedInvoice())}>
            <div className="col-auto px-1">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={openFileUpladDialogHandler}
              >
                <Icon icon="plus" />
              </button>
            </div>
          </RenderWrapper>
        </div>
      </FormGroup>
    </PageCard>
  );
};

export default InvoiceUploadSubForm;
